import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money, moneyTight, currencyCode, currencySymbol } from '../../utils/region';
import { currencyArgs } from '../../utils/currencyWord';
import { useCompanyRegions } from '../../utils/companyRegions';
import ClientPicker, { ClientOpt } from './ClientPicker';
import { displayCompanyName } from '../../utils/displayName';
import NumInput from './NumInput';
import DiscountInput from './DiscountInput';
import ReceiptEmailComposer from './ReceiptEmailComposer';
import FeeGapPrompt, { FeeGapData } from '../FeeGapPrompt';
import ApprovalBadge from '../ApprovalBadge';
import DocNoLink from '../DocNoLink';
import { TAB_GROUPS } from '../../tabPalette';
import { firmTabLabel, isLegalFirm } from '../../utils/firmKind';
import { NameBox, readClientGap, docNameGapText } from '../../utils/clientNameGap';   // title + first + last, or a company name (Bugify #be42fe7f)

type Firm = { id: string; name: string; short: string; kind?: string };
type Bank = { id: string; company_id: string; account_type: string; account_name: string; account_number: string };
type Item = { name: string; description: string; quantity: number; unit_price: number; discount: number; discount_pct?: number; disc_is_pct?: boolean; vat_rate: number; wht_rate: number; service_id?: string; is_note?: boolean; uid?: string };
type DocFile = { id: string; itemUid: string; fileName: string; size: number };
// Stable per-line key: item rows are recreated on every save, so attachments anchor to this instead.
const newUid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const WHT_RATES = [0, 1, 1.5, 2, 3, 5, 10, 15];
const r2 = (n: number) => Math.round(n * 100) / 100;
const firmLabel = (f: Firm) => firmTabLabel(f);
const isLegal = (f?: Firm) => isLegalFirm(f);
const today = () => new Date().toISOString().slice(0, 10);

const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 8, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const cell: React.CSSProperties = { ...inp, background: '#fff', padding: '7px 8px' };

// One receipt form for all entry points: standalone (from scratch), or seeded from an
// invoice / quotation. A receipt is a full document with its own line items; `amount`
// is the payment received (drives the parent's paid status when linked).
export default function ReceiptForm({ receiptId, seed, onClose, onSaved }: {
  receiptId?: string | null;
  seed?: { parentType: 'invoice' | 'quotation'; parent: any };   // parent row (InvoiceRow/QuotationRow)
  onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isNew = !receiptId;
  const [firms, setFirms] = useState<Firm[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [pickerIdx, setPickerIdx] = useState<number | null>(null);
  const pickerInputRef = useRef<HTMLInputElement | null>(null);
  const [pickerRect, setPickerRect] = useState<{ left: number; top: number; width: number } | null>(null);

  // link to a parent invoice/quotation (locks company + client)
  const [linked, setLinked] = useState(!!seed);
  const [parentType, setParentType] = useState<'invoice' | 'quotation' | ''>(seed?.parentType || '');
  const [parentId, setParentId] = useState<string>(seed?.parent?.id || '');
  const [refNo, setRefNo] = useState<string>(seed?.parent?.doc_no || '');

  const [companyId, setCompanyId] = useState(seed?.parent?.company_id || '');
  // Amounts in the issuing company's region (its own symbol / number locale when it has one).
  useCompanyRegions();
  const fmt = (n: any): string => moneyTight(n, 2, companyId);
  // {{curSym}} / {{cur}} in a sentence about THIS receipt's amounts: its company's currency, as fmt / money(…, companyId) — not the active company's
  const docCur = () => currencyArgs(currencyCode(companyId), currencySymbol(companyId));
  const [clientId, setClientId] = useState(seed?.parent?.client_id || '');
  // A new receipt asks for its client's complete name (Calem, Bugify #be42fe7f; utils/clientNameGap.ts), and an old
  // standalone one that takes another client — the server's own rule. The note under the picker says so as soon as the
  // form opens; Save reads the card again, so one corrected in another tab counts.
  const loadedClient = useRef('');   // the client an existing receipt was saved with
  const clientNameAsked = isNew || (!linked && clientId !== loadedClient.current);
  const [clientGap, setClientGap] = useState<{ id: string; boxes: NameBox[] } | null>(null);
  useEffect(() => {
    if (!clientId || !clientNameAsked) return;
    let dead = false;
    readClientGap(clientId).then(boxes => { if (!dead) setClientGap({ id: clientId, boxes }); });
    return () => { dead = true; };
  }, [clientId, clientNameAsked]);
  const clientGapNote = clientNameAsked && clientId && clientGap?.id === clientId && clientGap.boxes.length ? docNameGapText(t, clientGap.boxes) : '';
  const [issueDate, setIssueDate] = useState(today());
  // Owner 2026-08-29: the payment date is WHEN THE MONEY ARRIVED — a fact the operator has to state,
  // not a default. It starts blank and Issue Receipt refuses without it (same reasoning as the
  // invoice due date). It also drives the receipt NUMBER's month, so a wrong silent default
  // mis-numbers the document.
  const [paymentDate, setPaymentDate] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [remark, setRemark] = useState('');
  const [amount, setAmount] = useState(0);
  // Exchange/bank-fee gap (owner 2026-08-28): shown on a SAVED receipt whose payment did not
  // settle its parent, so a short international transfer can be closed out here too.
  const [feeGapData, setFeeGapData] = useState<FeeGapData | null>(null);
  const [mailPending, setMailPending] = useState(false);   // email frozen in the approval queue
  const amountTouched = useRef(false);
  const [items, setItems] = useState<Item[]>([]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [docNo, setDocNo] = useState('');
  // Batch 11 R3 (W2-10): this receipt's VAT mirror is in the books — only its remark and bank account are editable, and
  // Save sends the limited body (the server writes those alone and never re-syncs). null when it is free.
  const [mirrorLock, setMirrorLock] = useState<{ entries?: { docNo: string }[] } | null>(null);
  const [err, setErr] = useState('');
  const [dupRc, setDupRc] = useState<any[] | null>(null);   // "this money may already have a receipt"
  // Money in, ฿0 on the face of the receipt (owner 2026-09-17). dupOk/pb carry the answers to the
  // earlier questions forward so confirming this one does not un-answer them.
  const [zeroRc, setZeroRc] = useState<{ amount: number; dupOk: boolean } | null>(null);
  // "Yes, issue as PENDING BANK" is asked by window.confirm BEFORE these dialogs. Remember it so
  // answering a dialog does not re-ask a question the operator already answered (review 2026-09-17).
  const pbAsked = useRef(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);   // PDF render goes through headless Chrome (slow) → show a spinner
  const [showEmail, setShowEmail] = useState(false);   // receipt → email dialog (same as quotation/invoice)

  // Generate + open the PDF, with a "generating…" state so the user gets feedback.
  async function openPdf() {
    if (pdfBusy || !receiptId) return;
    setPdfBusy(true); setErr('');
    try { await api.openReceiptPdf(receiptId); }
    catch (e: any) { setErr(e.message); }
    finally { setPdfBusy(false); }
  }

  const defaultBank = (cid: string, bks: Bank[] = banks) => {
    const f = firms.find(x => x.id === cid);
    const want = f && isLegal(f) ? 'corporate' : 'personal';
    const b = bks.find(x => x.company_id === cid && x.account_type === want) || bks.find(x => x.company_id === cid);
    return b?.id || '';
  };
  const mapItems = (arr: any[]): Item[] => (arr || []).map((it: any) => ({
    name: it.name, description: it.description || '', quantity: it.quantity ?? 1, unit_price: it.unit_price ?? 0,
    discount: it.discount ?? 0, discount_pct: it.discount_pct || 0, disc_is_pct: (it.discount_pct || 0) > 0, vat_rate: it.vat_rate ?? 0, wht_rate: it.wht_rate ?? 0, service_id: it.service_id, is_note: !!it.is_note,
    uid: it.uid || newUid(),
  }));

  // initial load
  useEffect(() => {
    api.getInvoiceBanks().then((d: any) => {
      setFirms(d.companies || []); setBanks(d.banks || []);
      // Owner 2026-08-27: no auto-picked company — the operator must choose before saving.
      // A receipt linked to a document still inherits that document's company via its seed.
    }).catch(e => setErr(e.message));

    if (receiptId) {
      // edit existing receipt
      api.receiptLedgerLock(receiptId).then((d: any) => setMirrorLock(d?.lock || null)).catch(() => { /* the server still refuses */ });
      api.getReceipt(receiptId).then((r: any) => {
        loadedClient.current = r.client_id || '';
        setDocNo(r.doc_no); setCompanyId(r.company_id || ''); setClientId(r.client_id || '');
        setIssueDate(r.issue_date || today()); setPaymentDate(r.payment_date || r.issue_date || today());
        setBankAccountId(r.bank_account_id || ''); setRemark(r.remark || '');
        // follow the net if this was a full-payment receipt; preserve a partial amount
        setAmount(r.amount || 0); amountTouched.current = Math.abs((r.amount || 0) - (r.net_to_pay || 0)) > 0.01;
        setItems(mapItems(r.items));
        const lk = !!(r.invoice_id || r.quotation_id);
        setLinked(lk); setParentType(r.invoice_id ? 'invoice' : r.quotation_id ? 'quotation' : '');
        setParentId(r.invoice_id || r.quotation_id || '');
        setFeeGapData(r.fee_gap || null); setMailPending(!!r.pending_approval);
      }).catch(e => setErr(e.message));
    } else if (seed) {
      // new receipt seeded from an invoice / quotation. Amount auto-follows the
      // document net (so editing/deleting line items updates it) until the user
      // types an explicit amount — see the netToPay effect below.
      const getter = seed.parentType === 'invoice' ? api.getInvoice : api.getQuotation;
      getter(seed.parent.id).then((p: any) => {
        setItems(mapItems(p.items));
        setBankAccountId(p.bank_account_id || '');
      }).catch(e => setErr(e.message));
    }
  }, []); // eslint-disable-line

  // for standalone: when the company changes, load its clients + service catalog + default bank
  useEffect(() => {
    if (!companyId) return;
    const firm = firms.find(f => f.id === companyId);
    const which = firm && isLegal(firm) ? 'legal' : 'accounting';
    api.getClients({ firm: which, all: true }).then((d: any) => setClients(d.rows || [])).catch(() => {});
    api.getServices(companyId).then((s: any) => setServices(Array.isArray(s) ? s : [])).catch(() => {});
    if (isNew && !linked) setBankAccountId(b => b || defaultBank(companyId));
  }, [companyId, firms, banks]); // eslint-disable-line

  // Backfill each line's description from its service's catalog description when it's still empty.
  useEffect(() => {
    if (!services.length) return;
    setItems(prev => {
      let changed = false;
      const next = prev.map(it => {
        if (it.is_note || (it.description || '').trim() || !it.service_id) return it;
        const d = services.find((s: any) => s.id === it.service_id)?.description || '';
        if (!d) return it;
        changed = true; return { ...it, description: d };
      });
      return changed ? next : prev;
    });
  }, [services]); // eslint-disable-line

  // Pass-through (代收代付) services must NEVER appear on a receipt. When items are seeded from a parent
  // invoice/quotation (the "+"→Receipt flow) they may include such lines; drop them once we know the
  // catalog flags. (The picker + client-services auto-fill already exclude them at entry.)
  useEffect(() => {
    if (!services.length) return;
    const passIds = new Set(services.filter((s: any) => s.pass_through).map((s: any) => s.id));
    if (!passIds.size) return;
    setItems(prev => prev.some(it => it.service_id && passIds.has(it.service_id))
      ? prev.filter(it => !(it.service_id && passIds.has(it.service_id)))
      : prev);
  }, [services]); // eslint-disable-line

  // keep the service picker overlay aligned to its input (never clipped)
  useEffect(() => {
    if (pickerIdx === null) return;
    const recalc = () => { const el = pickerInputRef.current; if (!el) return; const r = el.getBoundingClientRect(); setPickerRect({ left: r.left, top: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [pickerIdx]);

  async function pickClient(id: string) {
    setClientId(id);
    if (!id) return;
    // Only auto-fill from the client's services when no line items exist yet — never
    // clobber items the user has already set up (editing, or after manual entry).
    const hasItems = items.some(it => (it.name || '').trim() || (it.description || '').trim() || it.unit_price > 0 || it.service_id);
    if (hasItems) return;
    try {
      const c: any = await api.getClient(id);
      // 代收代付 (pass-through) services are collected for a third party → never on a receipt; skip them.
      const passIds = new Set(services.filter((s: any) => s.pass_through).map((s: any) => s.id));
      setItems((c.services || []).filter((s: any) => !passIds.has(s.service_id)).map((s: any) => ({
        // Load the FULL multi-line note/description (e.g. the whole Due Diligence checklist), not just line 1.
        name: s.service_name, description: (s.note || s.service_desc || ''), quantity: s.quantity || 1,
        unit_price: s.unit_price || 0, discount: 0, vat_rate: s.vatable ? (s.vat_rate || 7) : 0, wht_rate: s.wht_rate || 0, service_id: s.service_id,
      })));
    } catch (e: any) { setErr(e.message); }
  }

  const upd = (i: number, patch: Partial<Item>) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  // Lines are an ordered mix of SERVICE rows and standalone NOTE rows (interleavable).
  const addItem = () => setItems(prev => [...prev, { name: '', description: '', quantity: 1, unit_price: 0, discount: 0, vat_rate: 0, wht_rate: 0, uid: newUid() }]);
  const addNote = () => setItems(prev => [...prev, { name: '', description: '', quantity: 0, unit_price: 0, discount: 0, vat_rate: 0, wht_rate: 0, is_note: true, uid: newUid() }]);

  // ── Line attachments (identical to quotations / invoices, owner 2026-08-09) ──
  // A brand-new receipt has no id yet, so files picked now are STAGED in memory and uploaded right
  // after Save creates it.
  const [docFiles, setDocFiles] = useState<DocFile[]>([]);
  const [upBusy, setUpBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<{ key: string; uid: string; file: File }[]>([]);
  const attachRef = useRef<HTMLInputElement>(null);
  const attachTarget = useRef<string>('');
  const loadDocFiles = async () => {
    if (!receiptId) return;
    try { const d: any = await api.docFiles('receipt', receiptId); setDocFiles(d.files || []); } catch { /* non-blocking */ }
  };
  useEffect(() => { void loadDocFiles(); }, [receiptId]); // eslint-disable-line
  const filesFor = (uid?: string) => docFiles.filter(f => uid && f.itemUid === uid);
  const pendingFor = (uid?: string) => pending.filter(p => uid && p.uid === uid);
  const dropPending = (key: string) => setPending(prev => prev.filter(p => p.key !== key));
  const pickAttach = (uid: string) => { attachTarget.current = uid; attachRef.current?.click(); };
  const onAttachPicked = async (fl: FileList | null) => {
    const uid = attachTarget.current;
    const list = Array.from(fl || []);
    if (!list.length || !uid) return;
    const tooBig = list.find(f => f.size > 50 * 1024 * 1024);
    if (tooBig) { setErr(t('qo_att_too_big', { name: tooBig.name })); return; }
    setErr('');
    if (!receiptId) { setPending(prev => [...prev, ...list.map(f => ({ key: newUid(), uid, file: f }))]); return; }
    setUpBusy(uid);
    try { for (const f of list) await api.docFileUpload('receipt', receiptId, uid, f); await loadDocFiles(); }
    catch (e: any) { setErr(e.message); } finally { setUpBusy(null); }
  };
  const delAttach = async (fileId: string) => {
    if (!window.confirm(t('qo_att_del_confirm'))) return;
    try { await api.docFileDelete(fileId); await loadDocFiles(); } catch (e: any) { setErr(e.message); }
  };
  const rmItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const move = (i: number, d: number) => setItems(prev => { const j = i + d; if (j < 0 || j >= prev.length) return prev; const a = [...prev]; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const moveTo = (from: number, to: number) => setItems(prev => { if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev; const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
  const arrowBtn = (disabled: boolean): React.CSSProperties => ({ width: 20, padding: 0, background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', color: disabled ? 'var(--border-strong)' : 'var(--text-tertiary)', fontSize: 14, lineHeight: 1 });
  const dragRail = (i: number) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 16 }}>
      <span draggable onDragStart={() => setDragIdx(i)} onDragEnd={() => setDragIdx(null)} title={t('cl_svc_reorder')}
        style={{ cursor: 'grab', color: 'var(--text-tertiary)', fontSize: 15, lineHeight: 1, userSelect: 'none' }}><i className="ti ti-grip-vertical" /></span>
      <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-tertiary)' }}>{i + 1}</span>
    </div>
  );

  const lineDisc = (it: Item) => it.disc_is_pct ? r2(it.quantity * it.unit_price * (it.discount_pct || 0) / 100) : (it.discount || 0);
  const calc = items.map(it => { if (it.is_note) return { pre: 0, vat: 0, wht: 0 }; const pre = r2(it.quantity * it.unit_price - lineDisc(it)); return { pre, vat: it.vat_rate > 0 ? r2(pre * it.vat_rate / 100) : 0, wht: r2(pre * it.wht_rate / 100) }; });
  const subtotal = r2(calc.reduce((s, c) => s + c.pre, 0));
  const vatTotal = r2(calc.reduce((s, c) => s + c.vat, 0));
  const totalAmount = r2(subtotal + vatTotal);
  const whtTotal = r2(calc.reduce((s, c) => s + c.wht, 0));
  const netToPay = r2(totalAmount - whtTotal);

  // until the user edits the amount, keep it in step with the document net
  useEffect(() => { if (!amountTouched.current) setAmount(netToPay); }, [netToPay]);

  // Owner 2026-08-29: the server answers 200 with needsDuplicateDecision when this client already
  // has a receipt for the same amount on the same day (their duplicate client record included).
  // Nothing is issued until the operator says it is genuinely a separate payment.
  async function save(duplicateConfirmed = false, zeroFaceConfirmed = false, pendingBankConfirmed = false) {
    // The limited edit: remark and bank account, written alone (batch 11 R3, W2-10).
    if (!isNew && mirrorLock) {
      setSaving(true); setErr('');
      try { await api.updateReceipt(receiptId!, { limited: 'books', remark, bankAccountId }); onSaved(); onClose(); }
      catch (e: any) { setErr(e.message); setSaving(false); }
      return;
    }
    if (!linked && !companyId) { setErr(t('iv_err_company')); return; }
    if (!paymentDate) { setErr(t('rc_err_pay_date')); return; }
    if (items.length === 0) { setErr(t('iv_err_line')); return; }
    setSaving(true); setErr('');
    // Refused here, naming the boxes; the server's same check is the backstop (Bugify #be42fe7f).
    if (clientNameAsked) {
      if (!clientId) { setErr(t('doc_client_required')); setSaving(false); return; }
      const boxes = await readClientGap(clientId);
      setClientGap({ id: clientId, boxes });
      if (boxes.length) { setErr(docNameGapText(t, boxes)); setSaving(false); return; }
    }
    const payload: any = {
      issueDate, paymentDate, bankAccountId, remark, amount,
      items: items.map(it => it.is_note
        ? { isNote: true, name: '', description: (it.description || '').trim(), quantity: 0, unitPrice: 0, discount: 0, vatable: false, vatRate: 0, whtRate: 0, uid: it.uid }
        : { serviceId: it.service_id, name: it.name, description: (it.description || '').replace(/\s+$/, ''), quantity: it.quantity, unitPrice: it.unit_price, discount: it.disc_is_pct ? 0 : it.discount, discountPct: it.disc_is_pct ? (it.discount_pct || 0) : 0, vatable: it.vat_rate > 0, vatRate: it.vat_rate, whtRate: it.wht_rate, uid: it.uid }),
    };
    if (linked) payload[parentType === 'invoice' ? 'invoiceId' : 'quotationId'] = parentId;
    else { payload.companyId = companyId; payload.clientId = clientId; }
    if (duplicateConfirmed) payload.duplicateConfirmed = true;
    if (zeroFaceConfirmed) payload.zeroFaceConfirmed = true;
    if (pendingBankConfirmed) payload.pendingBank = true;   // already answered on the first pass — don't ask twice
    try {
      let savedId = receiptId || '';
      if (isNew) {
        let r: any;
        let usedPb = pendingBankConfirmed;
        try { r = await api.createReceipt(payload); }
        catch (e: any) {
          // Owner 2026-08-18: the statement lags a day — with explicit confirmation the receipt is
          // issued NOW as PENDING BANK and linked from the match screen when the credit arrives.
          if (String(e?.message || '').includes('PENDING BANK') && window.confirm(
            'No matched bank payment yet (the credit usually appears TOMORROW).\n\nHas the client ALREADY transferred? Issue this receipt now as PENDING BANK?\n\nYou must link the bank credit in Bank · Accounting/Legal when it arrives ("Link to existing receipt").')) {
            usedPb = true; r = await api.createReceipt({ ...payload, pendingBank: true });
          } else throw e;
        }
        pbAsked.current = usedPb;
        if (r?.needsDuplicateDecision) { setDupRc(r.duplicateReceipts || []); setSaving(false); return; }
        if (r?.needsZeroFaceDecision) { setZeroRc({ amount: Number(r.amount) || 0, dupOk: duplicateConfirmed }); setSaving(false); return; }
        savedId = r?.id || '';
      }
      else {
        // The edit door raises the same ฿0 question (review 2026-09-17) — a 200 carrying it means
        // NOTHING was written, so it must not read as a saved receipt.
        const u: any = await api.updateReceipt(receiptId!, payload);
        if (u?.needsZeroFaceDecision) { setZeroRc({ amount: Number(u.amount) || 0, dupOk: duplicateConfirmed }); setSaving(false); return; }
      }
      // Anything staged before the receipt had an id. A failure must not read as success — the
      // receipt is already saved, so report it and keep the form open to retry.
      if (pending.length && savedId) {
        try { for (const p of pending) await api.docFileUpload('receipt', savedId, p.uid, p.file); setPending([]); }
        catch (e: any) { setErr(t('qo_att_upload_failed', { msg: e?.message || '' })); setSaving(false); return; }
      }
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 880, maxHeight: '94vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 16, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{isNew ? t('rc_new') : t('rc_edit')}</div>
          {/* Both numbers in the Quotation / Invoice / Receipt blue (Calem, Bugify #b4cccf02 — see DocNoLink); For
              Document's opens its quotation or invoice over this form. Were greens, #1FA971 and the app's accent. */}
          {docNo && <div style={{ marginLeft: 12, fontSize: 14, fontWeight: 700, color: TAB_GROUPS.peak.color }}>{docNo}{mailPending && <ApprovalBadge />}</div>}
          {refNo && <div style={{ marginLeft: 10, fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('rc_for_doc')}: <b><DocNoLink no={refNo} id={parentId} kind={parentType} /></b></div>}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px' }}>
          {mirrorLock && (
            <div data-ll-peak-receipt="" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, padding: '8px 11px', borderRadius: 8, background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E3A8A', fontSize: 12.5 }}>
              <i className="ti ti-lock" aria-hidden /><span>{t('tx_ll_peak_receipt', { docNos: (mirrorLock.entries || []).map(e => e.docNo).join(', ') })}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('iv_company')} <span style={{ color: '#DC2626' }}>*</span></label>
              <select value={companyId} onChange={e => { setCompanyId(e.target.value); setBankAccountId(defaultBank(e.target.value)); }} style={inp} disabled={linked || !isNew}>
                <option value="">—</option>{firms.map(f => <option key={f.id} value={f.id}>{firmLabel(f)}</option>)}
              </select></div>
            <div style={{ flex: 2 }}><label style={lbl}>{t('iv_client')}</label>
              <ClientPicker clients={clients} value={clientId} onPick={pickClient} disabled={linked || !companyId}
                placeholderEmpty={linked ? (displayCompanyName(seed?.parent?.client_name) || '—') : (companyId ? '—' : t('cp_pick_company'))}
                firm={(() => { const f = firms.find(x => x.id === companyId); return f && isLegal(f) ? 'legal' : 'accounting'; })()}
                onCreated={(c: ClientOpt) => setClients(prev => (prev.some(x => x.id === c.id) ? prev : [...prev, c]))} /></div>
          </div>
          {clientGapNote && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 14 }}>{clientGapNote}</div>}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('iv_issue')}</label><input type="date" value={issueDate} disabled={!!mirrorLock} onChange={e => setIssueDate(e.target.value)} style={inp} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('rc_pay_date')} <span style={{ color: '#DC2626' }}>*</span></label><input type="date" value={paymentDate} disabled={!!mirrorLock} onChange={e => setPaymentDate(e.target.value)} style={{ ...inp, ...(paymentDate ? null : { borderColor: '#DC2626' }) }} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('rc_received_at')}</label>
              <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)} style={inp}>
                <option value="">—</option><option value="cash">{t('rc_cash')}</option>{banks.filter(b => b.company_id === companyId).map(b => <option key={b.id} value={b.id}>{b.account_type === 'corporate' ? t('cs_corporate') : t('cs_personal')} · {b.account_name} · {b.account_number}</option>)}
              </select></div>
          </div>

          {/* Line items — shown, not edited, while the VAT mirror is in the books (batch 11 R3). */}
          <fieldset disabled={!!mirrorLock} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, padding: '8px 10px', background: 'var(--c-gray-50)', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
              <div style={{ width: 16 }} /><div style={{ flex: 1 }}>{t('iv_item')}</div><div style={{ width: 52 }}>{t('cl_qty')}</div><div style={{ width: 84 }}>{t('cl_unit_price')}</div><div style={{ width: 70 }}>{t('iv_disc')}</div><div style={{ width: 64 }}>VAT</div><div style={{ width: 74 }}>WHT</div><div style={{ width: 84, textAlign: 'right' }}>{t('iv_pre_vat')}</div><div style={{ width: 80 }} />
            </div>
            {items.map((it, i) => (
              <div key={i}
                onDragOver={dragIdx !== null ? (e) => e.preventDefault() : undefined}
                onDrop={dragIdx !== null ? () => { moveTo(dragIdx, i); setDragIdx(null); } : undefined}
                style={{ padding: '8px 10px', borderTop: '1px solid var(--border)', opacity: dragIdx === i ? 0.4 : 1 }}>
                {it.is_note ? (
                  /* ── standalone note line (independent of any service) ── */
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {dragRail(i)}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#B45309', flexShrink: 0 }}><i className="ti ti-note" />{t('cl_note')}</span>
                    {/* Bold, in the blue of the top-menu box this page sits in, as the note prints on the document —
                        invoicePdf's NOTE_BLUE (report ecfba54a); the box's own line round it. Was pink (Bugify #165ba961). */}
                    <input value={it.description} maxLength={1000} onChange={e => upd(i, { description: e.target.value })} placeholder={t('iv_note_ph')} style={{ ...cell, flex: 1, fontSize: 12.5, fontWeight: 700, color: TAB_GROUPS.peak.color, borderColor: TAB_GROUPS.peak.border }} />
                    <button onClick={() => move(i, -1)} disabled={i === 0} style={arrowBtn(i === 0)}><i className="ti ti-chevron-up" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === items.length - 1} style={arrowBtn(i === items.length - 1)}><i className="ti ti-chevron-down" /></button>
                    <button onClick={() => rmItem(i)} style={{ width: 24, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>
                  </div>
                ) : (<>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {dragRail(i)}
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input value={it.name} placeholder={t('iv_item_search')} style={{ ...cell, width: '100%', fontWeight: 600 }}
                        onChange={e => upd(i, { name: e.target.value })}
                        onFocus={e => { pickerInputRef.current = e.currentTarget; setPickerIdx(i); }}
                        onBlur={() => setTimeout(() => setPickerIdx(p => (p === i ? null : p)), 150)} />
                      {pickerIdx === i && pickerRect && (() => {
                        const matches = services.filter(s => !s.pass_through && (!it.name || (s.name || '').toLowerCase().includes(it.name.toLowerCase())));
                        // Owner 2026-08-25: same enlarged picker as InvoiceForm — min 440px wide, never
                        // past the viewport's right edge.
                        const ddW = Math.min(Math.max(pickerRect.width, 440), window.innerWidth - 24);
                        const ddLeft = Math.max(12, Math.min(pickerRect.left, window.innerWidth - ddW - 12));
                        return (
                          <div style={{ position: 'fixed', left: ddLeft, top: pickerRect.top + 2, width: ddW, zIndex: 10050, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', maxHeight: 320, overflowY: 'auto' }}>
                            {matches.slice(0, 40).map(s => (
                              <div key={s.id} onMouseDown={() => { upd(i, { name: s.name, unit_price: s.price, description: it.description || (s.description || ''), service_id: s.id }); setPickerIdx(null); }}
                                style={{ padding: '9px 12px', fontSize: 13.5, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.name}</span>
                                <span style={{ color: 'var(--text-tertiary)', flexShrink: 0, whiteSpace: 'nowrap' }}>{fmt(s.price)}</span>
                              </div>
                            ))}
                            {matches.length === 0 && <div style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('iv_no_service')}</div>}
                          </div>
                        );
                      })()}
                    </div>
                    <NumInput value={it.quantity} onChange={n => upd(i, { quantity: n })} style={{ ...cell, width: 52, textAlign: 'center' }} />
                    <NumInput value={it.unit_price} onChange={n => upd(i, { unit_price: n })} style={{ ...cell, width: 84 }} />
                    <DiscountInput isPct={!!it.disc_is_pct} value={it.disc_is_pct ? (it.discount_pct || 0) : (it.discount || 0)}
                      onChange={(isPct, v) => upd(i, isPct ? { disc_is_pct: true, discount_pct: v, discount: 0 } : { disc_is_pct: false, discount: v, discount_pct: 0 })} cellStyle={cell} companyId={companyId} />
                    <select value={it.vat_rate} onChange={e => upd(i, { vat_rate: Number(e.target.value) })} style={{ ...cell, width: 64 }}><option value={0}>None</option><option value={7}>7%</option></select>
                    <select value={it.wht_rate} onChange={e => upd(i, { wht_rate: Number(e.target.value) })} style={{ ...cell, width: 74 }}>{WHT_RATES.map(r => <option key={r} value={r}>{r === 0 ? 'None' : `${r}%`}</option>)}</select>
                    <div style={{ width: 84, textAlign: 'right', fontSize: 12.5, fontWeight: 600 }}>{fmt(calc[i]?.pre || 0)}</div>
                    <button onClick={() => move(i, -1)} disabled={i === 0} style={arrowBtn(i === 0)}><i className="ti ti-chevron-up" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === items.length - 1} style={arrowBtn(i === items.length - 1)}><i className="ti ti-chevron-down" /></button>
                    <button onClick={() => rmItem(i)} style={{ width: 24, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>
                  </div>
                  {/* the service's own description — auto-filled on pick, editable, shown under the service */}
                  <textarea value={it.description} onChange={e => upd(i, { description: e.target.value })} placeholder={t('iv_desc_ph')} rows={2}
                    style={{ width: '100%', marginTop: 6, padding: '6px 8px', fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--c-gray-50)', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    {filesFor(it.uid).map(f => (
                      <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 7, border: '1px solid #BFDBFE', background: '#EFF6FF', fontSize: 11.5, fontWeight: 600, color: TAB_GROUPS.peak.color, maxWidth: 260 }}>
                        <i className="ti ti-paperclip" style={{ fontSize: 12, flexShrink: 0 }} />
                        {/* A plain <a href> cannot carry the Authorization header, so this always
                            401'd (owner 2026-08-24). Same ticket route the document PDFs use. */}
                        <button type="button" onClick={() => api.openDocFile(f.id).catch((e: any) => alert(e?.message || 'Could not open the file'))} title={f.fileName}
                          style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName}</button>
                        <button onClick={() => delAttach(f.id)} title={t('fl_delete') || 'Delete'} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B42318', padding: 0, lineHeight: 1 }}>
                          <i className="ti ti-x" style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    {pendingFor(it.uid).map(pf => (
                      <span key={pf.key} title={t('qo_att_pending_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 7, border: '1px dashed #BFDBFE', background: '#F8FAFF', fontSize: 11.5, fontWeight: 600, color: TAB_GROUPS.peak.color, maxWidth: 260 }}>
                        <i className="ti ti-clock" style={{ fontSize: 12, flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pf.file.name}</span>
                        <button onClick={() => dropPending(pf.key)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B42318', padding: 0, lineHeight: 1 }}>
                          <i className="ti ti-x" style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    ))}
                    <button onClick={() => it.uid && pickAttach(it.uid)} disabled={upBusy === it.uid} title={t('qo_add_attach')}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 7, border: '1px dashed var(--border-strong)', background: 'var(--bg-app)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                      <i className={upBusy === it.uid ? 'ti ti-loader' : 'ti ti-paperclip'} style={{ fontSize: 12 }} />
                      {upBusy === it.uid ? t('fl_upload') + '…' : t('qo_add_attach')}
                    </button>
                  </div>
                </>)}
              </div>
            ))}
            <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
              <button onClick={addItem} style={{ flex: 1, padding: '9px', border: 'none', background: '#fff', color: 'var(--accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>＋ {t('iv_add_item')}</button>
              <button onClick={addNote} style={{ flex: 1, padding: '9px', border: 'none', borderLeft: '1px solid var(--border)', background: '#fff', color: '#B45309', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>＋ {t('iv_add_note')}</button>
              {(() => {
                // Attaches to the LAST service line (notes can't carry files).
                const last = [...items].reverse().find(x => !x.is_note);
                const ready = !!last?.uid;
                return (
                  <button onClick={() => last?.uid && pickAttach(last.uid)} disabled={!ready}
                    title={!last ? t('qo_att_need_item') : `${t('qo_add_attach')} → ${last.name || t('iv_item')}`}
                    style={{ flex: 1, padding: '9px', border: 'none', borderLeft: '1px solid var(--border)', background: '#fff', color: ready ? TAB_GROUPS.peak.color : 'var(--text-tertiary)', fontWeight: 700, fontSize: 13, cursor: ready ? 'pointer' : 'not-allowed' }}>
                    ＋ {t('qo_add_attach')}
                  </button>
                );
              })()}
            </div>
            <input ref={attachRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => { void onAttachPicked(e.target.files); e.currentTarget.value = ''; }} />
          </div>
          </fieldset>

          {/* Remark + totals */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('iv_remark')}</label>
              <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={4} style={{ ...inp, resize: 'vertical' }} placeholder={t('iv_remark_ph')} /></div>
            <div style={{ width: 260, fontSize: 13 }}>
              <Row label={t('iv_subtotal')} val={fmt(subtotal)} />
              <Row label={`VAT`} val={fmt(vatTotal)} />
              <Row label={t('iv_total')} val={fmt(totalAmount)} bold />
              <Row label={t('iv_wht')} val={'-' + fmt(whtTotal)} />
              <div style={{ borderTop: '2px solid var(--border)', marginTop: 6, paddingTop: 8 }}><Row label={t('iv_net')} val={fmt(netToPay)} /></div>
              <div style={{ marginTop: 10 }}>
                <fieldset disabled={!!mirrorLock} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
                <label style={lbl}>{t('rc_amount')} *</label>
                <NumInput value={amount} onChange={n => { amountTouched.current = true; setAmount(n); }}
                  style={{ ...inp, fontWeight: 800, fontSize: 16, color: TAB_GROUPS.peak.color }} />
                </fieldset>
              </div>
            </div>
          </div>
          {!isNew && receiptId && feeGapData && (feeGapData.tier !== 'none' || !!feeGapData.feeAmount) && (
            <div style={{ marginTop: 14 }}>
              <FeeGapPrompt receiptId={receiptId} gap={feeGapData} onChanged={() => {
                api.receiptFeeGap(receiptId).then((g: any) => setFeeGapData(g)).catch(() => {});
                onSaved();
              }} />
            </div>
          )}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginTop: 12 }}>{err}</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px', borderTop: '1px solid var(--border)' }}>
          {!isNew && <button onClick={openPdf} disabled={pdfBusy} style={{ padding: '9px 16px', borderRadius: 10, border: `1px solid ${TAB_GROUPS.peak.color}`, background: 'transparent', color: TAB_GROUPS.peak.color, fontWeight: 700, fontSize: 13, cursor: pdfBusy ? 'wait' : 'pointer', opacity: pdfBusy ? 0.75 : 1 }}>
            {pdfBusy
              ? <><i className="ti ti-loader-2" style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />{t('iv_pdf_gen')}</>
              : <><i className="ti ti-file-type-pdf" style={{ marginRight: 6 }} />PDF</>}
          </button>}
          {!isNew && <button onClick={() => setShowEmail(true)} style={{ padding: '9px 16px', borderRadius: 10, border: `1px solid ${TAB_GROUPS.peak.color}`, background: 'transparent', color: TAB_GROUPS.peak.color, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <i className="ti ti-mail" style={{ marginRight: 6 }} />{t('iv_email')}
          </button>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={() => save()} disabled={saving} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: TAB_GROUPS.peak.color, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>{isNew ? t('rc_save') : t('save')}</button>
        </div>
      </div>
      {dupRc && dupRc.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setDupRc(null); }}>
          <div style={{ width: 560, maxWidth: '100%', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 800, color: '#B45309' }}>
              <i className="ti ti-alert-triangle" style={{ marginRight: 7 }} />{t('rc_dup_title')}
            </div>
            <div style={{ padding: '14px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>{t('rc_dup_hint')}</div>
              {dupRc.map((d: any) => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 11px', border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 9, marginBottom: 7, fontSize: 12.5 }}>
                  <span><b>{d.doc_no}</b> · {d.client_name}</span>
                  <span style={{ whiteSpace: 'nowrap' }}>{fmt(d.amount)} · {d.payment_date}{d.created_by_name ? ` · ${d.created_by_name}` : ''}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
              <button onClick={() => setDupRc(null)} style={{ padding: '8px 16px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
              <button onClick={() => { setDupRc(null); save(true, false, pbAsked.current); }} style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: '#B45309', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('rc_dup_anyway')}</button>
            </div>
          </div>
        </div>
      )}
      {zeroRc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setZeroRc(null); }}>
          <div style={{ width: 560, maxWidth: '100%', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 800, color: '#B45309' }}>
              <i className="ti ti-alert-triangle" style={{ marginRight: 7 }} />{t('rc_zero_title', docCur())}
            </div>
            <div style={{ padding: '14px 20px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
              {t('rc_zero_hint', { amt: money(zeroRc.amount, 2, companyId), ...docCur() })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
              <button onClick={() => setZeroRc(null)} style={{ padding: '8px 16px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
              <button onClick={() => { const { dupOk } = zeroRc; setZeroRc(null); save(dupOk, true, pbAsked.current); }} style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: '#B45309', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('rc_zero_anyway', docCur())}</button>
            </div>
          </div>
        </div>
      )}
      {showEmail && receiptId && <ReceiptEmailComposer receiptId={receiptId} defaultName={clients.find((c: any) => c.id === clientId)?.name || seed?.parent?.client_name || ''} onClose={() => setShowEmail(false)} />}
    </div>
  );
}

function Row({ label, val, bold }: { label: string; val: string; bold?: boolean }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: 'var(--text-secondary)' }}>
    <span style={{ fontWeight: bold ? 700 : 400 }}>{label}</span>
    <span style={{ fontWeight: bold ? 800 : 500, color: 'var(--text-primary)' }}>{val}</span>
  </div>;
}
